import type { OptionType } from "components/inputs";
import { MultiSelect } from "components/inputs";
import isNumber from "lodash/isNumber";
import keyBy from "lodash/keyBy";
import type { FieldErrors } from "react-hook-form";

type ServiceMultiSelectProps = {
  onChange: (options: OptionType[]) => void;
  errors: FieldErrors;
  options: OptionType[];
  value: OptionType[] | undefined;
  fileData?: {
    status?: string;
    services?: OptionType[];
  };
  required?: boolean;
  disableRemove?: boolean;
  label?: string;
  name?: string;
  readOnly?: boolean;
};

export const ServiceMultiSelect = ({
  onChange,
  errors,
  options,
  value,
  fileData,
  required = true,
  disableRemove = false,
  label = "Services Selected",
  name = "services",
  readOnly = false,
}: ServiceMultiSelectProps) => {
  const getValue = () => {
    if (!value) return [];

    const mappedValues = value.map(
      (selectedOption: {
        value: number | string;
        label: string;
        type_verbose?: string;
      }) => {
        // return the original option if it's not a numeric value
        if (!isNumber(selectedOption.value)) return selectedOption;

        // for numeric values, prioritize type_verbose if available
        if (selectedOption.type_verbose) {
          return { ...selectedOption, label: selectedOption.type_verbose };
        }

        // otherwise look up the correct label from options
        const optionsMap = keyBy(options, "value");
        const correctLabel =
          optionsMap[selectedOption.label]?.label || selectedOption.label;

        return { ...selectedOption, label: correctLabel };
      },
    );

    // sort alphabetically by label
    return mappedValues.sort((a, b) => a.label.localeCompare(b.label));
  };

  // prevent deletion when:
  // 1. file is open/completed ("O" or "C"), OR
  // 2. there is only one service remaining
  // 3. or if disableRemove is explicitly set
  const shouldDisableRemove =
    disableRemove ||
    (fileData
      ? (fileData.status && fileData.status !== "U") || // not undefined or "U"
        fileData.services?.length === 1
      : false);

  return (
    <MultiSelect
      label={label}
      name={name}
      errors={errors}
      options={options}
      value={getValue()}
      onChange={onChange}
      required={required}
      readOnly={readOnly}
      disableClearAll={true}
      disableRemoveCondition={shouldDisableRemove}
    />
  );
};

const MISCELLANEOUS_SERVICE_VALUES = ["CON", "AUD", "S111G", "SWL"];

// Already-saved services come back from the API with `value` set to the
// numeric service id and `label` set to the type code (e.g. "S111G").
// Unsaved options from the licensed-service list have `value` set to the
// type code. Match either shape.
const serviceHasCode = (service: OptionType, codes: string[]) =>
  (typeof service.value === "string" && codes.includes(service.value)) ||
  (typeof service.label === "string" && codes.includes(service.label));

export const getFilteredServiceOptions = (
  selectedServices: OptionType[],
  serviceOptions: OptionType[],
) => {
  const hasSection111 = selectedServices.some((service) =>
    serviceHasCode(service, ["111"]),
  );

  // Section 111 is exclusive - only show this option when selected
  if (hasSection111) {
    return serviceOptions.filter((service) => service.value === "111");
  }

  // Codes of misc services already on the file. For unsaved options the
  // code lives in `value`; for saved services it lives in `label` (since
  // `value` is the numeric service id). Same misc type can't be added
  // twice to one file.
  const selectedMiscCodes = new Set(
    selectedServices
      .map((service) => {
        if (
          typeof service.value === "string" &&
          MISCELLANEOUS_SERVICE_VALUES.includes(service.value)
        ) {
          return service.value;
        }
        if (
          typeof service.label === "string" &&
          MISCELLANEOUS_SERVICE_VALUES.includes(service.label)
        ) {
          return service.label;
        }
        return null;
      })
      .filter((code): code is string => code !== null),
  );

  // Miscellaneous services are exclusive - only show these when selected,
  // and never the misc type(s) already on the file.
  if (selectedMiscCodes.size > 0) {
    return serviceOptions.filter(
      (service) =>
        typeof service.value === "string" &&
        MISCELLANEOUS_SERVICE_VALUES.includes(service.value) &&
        !selectedMiscCodes.has(service.value),
    );
  }

  const hasLienService = selectedServices.some(
    (service) =>
      (typeof service.value === "string" && service.value === "LIEN") ||
      (typeof service.label === "string" && service.label === "LIEN"),
  );

  const hasDocsService = selectedServices.some(
    (service) =>
      (typeof service.value === "string" && service.value === "DOCS") ||
      (typeof service.label === "string" && service.label === "DOCS"),
  );

  // default case - filter out restricted services and duplicates
  return serviceOptions.filter((service) => {
    const isNotRestrictedService =
      service.value !== "111" &&
      !(
        typeof service.value === "string" &&
        MISCELLANEOUS_SERVICE_VALUES.includes(service.value)
      );

    // don't allow duplicate LIEN services
    const isNotDuplicateLien = !(hasLienService && service.value === "LIEN");

    // don't allow duplicate DOCS services
    const isNotDuplicateDocs = !(hasDocsService && service.value === "DOCS");

    return isNotRestrictedService && isNotDuplicateLien && isNotDuplicateDocs;
  });
};
